/**
 * Wallet Transactions Repository
 *
 * Handles CRUD operations and real-time listeners for transaction documents
 * stored at users/{uid}/wallet/summary/transactions/{transactionId}
 *
 * Tracks payments (money going out) and deposits (money coming in).
 * Records are immutable (write-once) - no update method provided.
 */

import {
  DocumentSnapshot,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  deleteDoc,
  where,
} from 'firebase/firestore';
import { Paths, Page, PaginationOptions, withBackoff, setDoc, getDoc } from './firestoreUtils';
import { addUserScopedWriteGuard } from '../lib/identity/writes';
import type { Transaction, TransactionType, TransactionStatus, TransactionMethod } from '../types/transactions';
import {
  TransactionDoc,
  toTransactionDoc,
  fromTransactionDoc,
  generateWalletItemId,
} from '../types/walletFirestore';
import { shouldTagAsTest } from '../config/agenticWorkflowConfig';

// ============================================================================
// Types
// ============================================================================

export interface CreateTransactionInput {
  /** Transaction type: payment (out) or deposit (in) */
  type: TransactionType;
  /** Summary description */
  summary: string;
  /** Member ID */
  memberId: string;
  /** Amount in USD (always positive) */
  amountUsd: number;
  /** Amount in OLV (snapshot) */
  amountOlv: number;
  /** OLV exchange rate at time of transaction */
  olvRateAtTime: number;
  /** Transaction method */
  method: TransactionMethod;
  /** Tags for categorization */
  tags?: string[];
  /** External reference ID */
  externalRef?: string;
  /** Initial status (defaults to 'pending') */
  status?: TransactionStatus;
}

export interface ListTransactionsOptions extends PaginationOptions {
  /** Filter by status */
  status?: TransactionStatus;
  /** Filter by type */
  type?: TransactionType;
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Create a new transaction record (instant write, no debounce)
 * @returns The created transaction with generated ID
 */
export async function createTransaction(uid: string, input: CreateTransactionInput): Promise<Transaction> {
  addUserScopedWriteGuard(uid, 'createTransaction');

  const id = generateWalletItemId();
  const timestamp = Date.now();

  const baseTags = input.tags || [];
  const tags = shouldTagAsTest() && !baseTags.includes('test example')
    ? [...baseTags, 'test example']
    : baseTags;

  const transaction: Transaction = {
    id,
    timestamp,
    type: input.type,
    tags,
    summary: input.summary,
    memberId: input.memberId,
    amountUsd: input.amountUsd,
    amountOlv: input.amountOlv,
    olvRateAtTime: input.olvRateAtTime,
    method: input.method,
    externalRef: input.externalRef,
    status: input.status || 'pending',
  };

  const docData = {
    ...toTransactionDoc(transaction),
    created_at: serverTimestamp(),
  };

  const ref = Paths.walletTransactionDoc(uid, id);
  await withBackoff(() => setDoc(ref, docData));

  return transaction;
}

/**
 * Get a single transaction by ID
 */
export async function getTransaction(uid: string, id: string): Promise<Transaction | null> {
  const ref = Paths.walletTransactionDoc(uid, id);
  const snap = await withBackoff(() => getDoc(ref));
  if (!snap.exists()) return null;
  return fromTransactionDoc(snap.data() as TransactionDoc);
}

/**
 * List transactions with pagination (newest first)
 */
export async function listTransactions(
  uid: string,
  opts?: ListTransactionsOptions
): Promise<Page<Transaction>> {
  const col = Paths.walletTransactionsCol(uid);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts?.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Filter by type if provided
  if (opts?.type) {
    parts.push(where('type', '==', opts.type));
  }

  // Always order by timestamp descending (newest first)
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  if (opts?.limit) {
    parts.push(limit(opts.limit));
  }

  // Build base query
  let q = query(col, ...parts);

  // Apply cursor if provided
  if (opts?.startAfter) {
    const { startAfter: fbStartAfter } = await import('firebase/firestore');
    q = query(q, fbStartAfter(opts.startAfter as DocumentSnapshot));
  }

  const snap = await withBackoff(() => getDocs(q));
  const items = snap.docs.map((d) => fromTransactionDoc(d.data() as TransactionDoc));
  const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;

  return { items, nextCursor };
}

/**
 * Delete a transaction (admin only, if ever needed)
 */
export async function deleteTransaction(uid: string, id: string): Promise<void> {
  addUserScopedWriteGuard(uid, 'deleteTransaction');
  const ref = Paths.walletTransactionDoc(uid, id);
  await withBackoff(() => deleteDoc(ref));
}

/**
 * Delete all transactions with a specific tag
 * @param uid User ID
 * @param tag Tag to filter by (e.g., "test example")
 * @returns Number of deleted transactions
 */
export async function deleteTransactionsByTag(uid: string, tag: string): Promise<number> {
  addUserScopedWriteGuard(uid, 'deleteTransactionsByTag');

  const col = Paths.walletTransactionsCol(uid);
  const q = query(col, where('tags', 'array-contains', tag));
  const snap = await withBackoff(() => getDocs(q));

  let deletedCount = 0;
  for (const doc of snap.docs) {
    await withBackoff(() => deleteDoc(doc.ref));
    deletedCount++;
  }

  return deletedCount;
}

// ============================================================================
// Real-time Listeners
// ============================================================================

/**
 * Subscribe to transactions with real-time updates
 * @param uid User ID
 * @param opts Options including limit and optional status/type filters
 * @param onData Callback with updated transactions list and cursor
 * @param onError Optional error callback
 * @returns Unsubscribe function
 */
export function onTransactionsSnapshot(
  uid: string,
  opts: { limit: number; status?: TransactionStatus; type?: TransactionType },
  onData: (items: Transaction[], nextCursor: DocumentSnapshot | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const col = Paths.walletTransactionsCol(uid);

  // Build query parts
  const parts: any[] = [];

  // Filter by status if provided
  if (opts.status) {
    parts.push(where('status', '==', opts.status));
  }

  // Filter by type if provided
  if (opts.type) {
    parts.push(where('type', '==', opts.type));
  }

  // Order by timestamp descending
  parts.push(orderBy('timestamp', 'desc'));

  // Apply limit
  parts.push(limit(opts.limit));

  const q = query(col, ...parts);

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => fromTransactionDoc(d.data() as TransactionDoc));
      const nextCursor = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
      onData(items, nextCursor);
    },
    (error) => {
      console.error('Transactions snapshot error:', error);
      onError?.(error);
    }
  );
}
